import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ReturnDeadline, ReturnDeadlineKind, ReturnEvent } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import { deadlineText } from "../../utils/returnsRules";

const PREVIEW_CHARS = 120;

const DEADLINE_LABELS: Record<ReturnDeadlineKind, string> = {
  respond: "Respond by",
  post: "Buyer to post by",
  buyer_respond: "Buyer to respond by",
  refund: "Refund by",
};

function humanise(code: string) {
  const words = code.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function preview(body: string | null) {
  if (!body) return "";
  const flat = body.replace(/\s+/g, " ").trim();
  return flat.length > PREVIEW_CHARS ? `${flat.slice(0, PREVIEW_CHARS)}…` : flat;
}

interface ReturnTimelineProps {
  events: ReturnEvent[];
  deadline: ReturnDeadline;
  now: DateTime;
}

/** eBay and Wilma events in one list, oldest first, with the next eBay deadline last (amber or red when it's ours to meet, grey when it's the buyer's). */
export default function ReturnTimeline({ events, deadline, now }: ReturnTimelineProps) {
  const { t } = useTranslation("returns");
  const next = deadlineText(deadline, now);

  // The API's title heads the row when it has one; messages and notes still show their preview after it.
  const describe = (e: ReturnEvent) => {
    const author = e.authorName ? ` · ${e.authorName}` : "";
    switch (e.kind) {
      case "BuyerMessage":
        return `${e.title || t("Message from buyer")}${e.body ? `: "${preview(e.body)}"` : ""}${e.attachments?.length ? ` · ${t("{{count}} photos", { count: e.attachments.length })}` : ""}`;
      case "SellerMessage":
        return `${e.title || `${t("Message to buyer")}${author}`}${e.body ? `: "${preview(e.body)}"` : ""}`;
      case "Note":
        return `${e.title || `${t("Note")}${author}`}${e.body ? `: ${preview(e.body)}` : ""}`;
      case "EbayActivity":
        if (e.title) return e.activity ? `${e.title} · eBay` : e.title;
        return `${e.body || (e.activity ? humanise(e.activity) : t("eBay update"))}${e.toState ? ` · ${humanise(e.toState)}` : ""} · eBay`;
      default:
        return e.title || `${e.body ?? (e.activity ? humanise(e.activity) : "")}${author}`;
    }
  };

  const highlight = (e: ReturnEvent) => e.toState === "RETURN_REQUESTED" || e.activity === "RETURN_REQUESTED";

  return (
    <article className="flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-gray-900">{t("Timeline")}</h2>
        <span className="text-[11px] text-gray-500">{t("eBay and Wilma events together")}</span>
      </div>
      <ol className="flex flex-col text-[12px]">
        {events.map((e, i) => {
          const last = i === events.length - 1 && !next;
          return (
            <li key={e.id} className="grid grid-cols-[150px_16px_minmax(0,1fr)] gap-x-2.5">
              <span className="py-1 text-right text-gray-500">{formatDateTime(e.at, INBOX_DAY_TIME)}</span>
              <span className="flex flex-col items-center">
                <span className={classNames("mt-[9px] h-2 w-2 rounded-full", highlight(e) ? "bg-amber-700" : "bg-gray-300")} />
                {!last && <span className="w-0.5 flex-1 bg-gray-200" />}
              </span>
              <span className={classNames("py-1 pb-2.5", highlight(e) ? "font-medium text-gray-900" : "text-gray-700")}>{describe(e)}</span>
            </li>
          );
        })}
        {next && deadline.at && (
          <li className="grid grid-cols-[150px_16px_minmax(0,1fr)] gap-x-2.5">
            <span className={classNames("py-1 text-right font-semibold", next.tone === "red" ? "text-red-700" : next.tone === "amber" ? "text-amber-700" : "text-gray-600")}>{formatDateTime(deadline.at, INBOX_DAY_TIME)}</span>
            <span className="flex flex-col items-center">
              <span className={classNames("mt-[9px] h-2 w-2 rounded-full border-2 border-dashed", next.tone === "red" ? "border-red-700" : next.tone === "amber" ? "border-amber-700" : "border-gray-400")} />
            </span>
            <span className={classNames("py-1 font-semibold", next.tone === "red" ? "text-red-700" : next.tone === "amber" ? "text-amber-700" : "text-gray-600")}>
              {deadline.label || (deadline.kind ? t(DEADLINE_LABELS[deadline.kind]) : "")} · {next.tone === "red" ? t("eBay deadline passed") : t("eBay deadline")}
            </span>
          </li>
        )}
        {events.length === 0 && !next && <li className="text-gray-500">{t("Nothing yet.")}</li>}
      </ol>
    </article>
  );
}
